import { Button } from "@groot/ui/button";
import { Page } from "@/app/shared/components/page";
import { ModelSelect } from "@/app/shared/components/model-select";
import { PageHeader } from "@groot/shell/components/layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { Checkbox } from "@groot/ui/checkbox";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Account, Statement as StatementType } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, Download, Edit2, Maximize2, Trash2, Upload, BoxSelect } from "lucide-react";
import { SafePdfViewer } from "./components/SafePdfViewer";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** The extractor's JSON as stored on `Statement.extractedData`. */
interface ExtractedData {
  /** Older extractions stored the full account number at the top level. */
  accountNumber?: string | null;
  metadata: {
    institutionName?: string | null;
    accountType?: string;
    lastFourAccountNumber?: string | null;
    statementDate?: string | null;
    billingPeriodStart?: string | null;
    billingPeriodEnd?: string | null;
    currency?: string | null;
  };
  summary: {
    openingBalance?: number | null;
    closingBalance?: number | null;
    totalDebits?: number | null;
    totalCredits?: number | null;
    paymentDueDate?: string | null;
    minimumPaymentDue?: number | null;
    totalAmountDue?: number | null;
    totalCreditLimit?: number | null;
    availableCreditLimit?: number | null;
    cashLimit?: number | null;
  };
  rewards?: {
    openingBalance?: number | null;
    earned?: number | null;
    redeemed?: number | null;
    closingBalance?: number | null;
    unit?: string | null;
  } | null;
  transactions: Transaction[];
}

interface Transaction {
  date: string;
  description: string;
  amount: number;
  type: "credit" | "debit";
  referenceNo?: string;
  balance?: number;
}

/** Types the statement's loosely-typed `extractedData` JSON for display. */
const toExtractedData = (value: StatementType["extractedData"]): ExtractedData | null =>
  (value ?? null) as ExtractedData | null;

// Safe date formatting helper
const formatSafeDate = (
  dateString: string | null | undefined,
  formatStr = "MMM DD, YYYY",
): string => {
  if (!dateString) return "N/A";

  const date = dayjs(dateString);
  if (!date.isValid()) {
    return dateString;
  }

  return date.format(formatStr);
};

const formatMonth = (month: string | null | undefined) => {
  if (!month) return "N/A";
  return dayjs(month, "YYYY-MM").format("MMMM YYYY");
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return "Unknown";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

export function StatementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [statement, setStatement] = useState<StatementType | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [autoUploadAfterExtract, setAutoUploadAfterExtract] = useState(true);
  const [renameFileAfterExtract, setRenameFileAfterExtract] = useState(false);
  const [ignorePagesFromEnd, setIgnorePagesFromEnd] = useState<number | undefined>(undefined);
  const [aiModel, setAiModel] = useState<string | undefined>(undefined);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadStatement and loadAccounts are not stable
  useEffect(() => {
    loadStatement();
    loadAccounts();
  }, [id]);

  useEffect(() => {
    if (statement?.accountId && accounts.length > 0) {
      const account = accounts.find((a) => a.id === statement.accountId);
      const extractorConfig = account?.meta?.statementExtractor as
        | Record<string, unknown>
        | undefined;
      const defaultPages = extractorConfig?.defaultPagesToIgnoreFromEnd;
      if (typeof defaultPages === "number") {
        setIgnorePagesFromEnd(defaultPages);
      }
    }
  }, [statement, accounts]);

  const loadStatement = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getStatement(Number(id));
      setStatement(data);
      const extracted = toExtractedData(data.extractedData);
      if (extracted?.transactions) {
        setTransactions(extracted.transactions);
      }
      if (data.accountId) {
        setSelectedAccountId(data.accountId.toString());
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to load statement",
        variant: "destructive",
      });
      navigate("/statements");
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await apiClient.getAccounts();
      setAccounts(data.filter((acc: Account) => !acc.isDeleted && !acc.isArchived));
    } catch {
      toast({
        title: "Error",
        description: "Failed to load accounts",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!selectedAccountId) {
      toast({
        title: "No account selected",
        description: "Please select an account to import transactions",
        variant: "destructive",
      });
      return;
    }

    try {
      setImporting(true);
      await apiClient.importStatementTransactions(Number(id), {
        transactions,
        accountId: Number(selectedAccountId),
      });

      toast({
        title: "Success",
        description: `Queued ${transactions.length} transactions for import`,
      });

      loadStatement();
    } catch (error) {
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to import transactions",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExportCSV = () => {
    if (!transactions.length) return;

    const headers = ["Date", "Description", "Amount", "Type", "Reference No", "Balance"];
    const csvData = [
      headers.join(","),
      ...transactions.map((t) =>
        [
          t.date,
          `"${t.description.replace(/"/g, '""')}"`,
          t.amount,
          t.type,
          t.referenceNo || "",
          t.balance || "",
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${statement?.fileName || "statement"}-transactions.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Success",
      description: "CSV file downloaded",
    });
  };

  const handleConfirmExtract = async () => {
    try {
      setExtracting(true);
      await apiClient.extractStatement(Number(id), {
        autoUpload: autoUploadAfterExtract,
        accountId: selectedAccountId ? Number(selectedAccountId) : undefined,
        ignorePagesFromEnd,
        aiModel,
        renameFile: renameFileAfterExtract,
      });
      toast({
        title: "Queued",
        description: "Statement extraction queued",
      });
      await loadStatement();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to queue extraction",
        variant: "destructive",
      });
    } finally {
      setExtracting(false);
      setExtractDialogOpen(false);
    }
  };

  const updateTransaction = (index: number, field: string, value: string | number) => {
    const updated = [...transactions];
    updated[index] = { ...updated[index], [field]: value };
    setTransactions(updated);
  };

  const deleteTransaction = (index: number) => {
    setTransactions(transactions.filter((_, i) => i !== index));
    toast({
      title: "Deleted",
      description: "Transaction removed",
    });
  };

  const saveExtractedData = async () => {
    try {
      await apiClient.updateStatementExtractedData(Number(id), {
        ...statement?.extractedData,
        transactions,
      });
      toast({
        title: "Saved",
        description: "Changes saved successfully",
      });
      setEditingIndex(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!statement) {
    return (
      <Page>
        <p>Statement not found</p>
      </Page>
    );
  }

  const isArchival = statement.statementType === "archival";
  const extractedData = toExtractedData(statement.extractedData);

  return (
    <Page>
      {/* Header */}
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <PageHeader
          title={
            statement.month
              ? `${statement.account?.accountName || "Account"} - ${formatMonth(statement.month)}`
              : statement.fileName
          }
          description={`Uploaded ${formatSafeDate(statement.uploadedAt, "MMM DD, YYYY hh:mm A")}`}
          actions={
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate(`/statements/${statement.id}/annotate`)}
                className="gap-2"
              >
                <BoxSelect className="size-4" />
                Extract transactions
              </Button>
              <Button
                variant="outline"
                onClick={() => setExtractDialogOpen(true)}
                disabled={extracting}
                className="gap-2"
              >
                {extracting ? <LoadingSpinner className="size-4" /> : <Upload className="size-4" />}
                Extract whole PDF
              </Button>
              <Button asChild>
                <a
                  href={apiClient.getStatementPdfUrl(statement.id)}
                  download={statement.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="size-4" />
                  Download PDF
                </a>
              </Button>
            </div>
          }
        />
      </div>

      {isArchival ? (
        // Archival Statement View
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Details Section */}
          <div>
            <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-muted-foreground">Account</dt>
                <dd className="text-sm text-foreground mt-1">
                  {statement.account?.accountName || "Unknown Account"}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Type</dt>
                <dd className="text-sm text-foreground mt-1">Archival</dd>
              </div>
              {statement.month && (
                <div>
                  <dt className="text-sm text-muted-foreground">Month</dt>
                  <dd className="text-sm text-foreground mt-1">{formatMonth(statement.month)}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-muted-foreground">File size</dt>
                <dd className="text-sm text-foreground mt-1">
                  {formatFileSize(statement.fileSize)}
                </dd>
              </div>
              {statement.notes && (
                <div className="pt-4 border-t border-border">
                  <dt className="text-sm text-muted-foreground">Notes</dt>
                  <dd className="text-sm text-foreground mt-1 whitespace-pre-wrap">
                    {statement.notes}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* PDF Viewer */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-foreground">PDF Document</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullScreen(true)}
                className="gap-2"
              >
                <Maximize2 className="size-4" />
                Full screen
              </Button>
            </div>
            <div className="border border-border rounded-lg overflow-hidden bg-muted">
              <div className="w-full h-[70vh]">
                <SafePdfViewer pdfUrl={apiClient.getStatementPdfUrl(statement.id)} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Extraction Statement View
        <div className="space-y-6">
          {/* Statement Info */}
          <div>
            <h2 className="text-sm font-medium text-foreground mb-4">Statement information</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="text-sm text-foreground mt-1">Extraction</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-sm text-foreground mt-1 capitalize">{statement.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">File size</p>
                <p className="text-sm text-foreground mt-1">{formatFileSize(statement.fileSize)}</p>
              </div>
              {extractedData?.accountNumber && (
                <div>
                  <p className="text-sm text-muted-foreground">Account number</p>
                  <p className="text-sm text-foreground font-mono mt-1">
                    {extractedData.accountNumber}
                  </p>
                </div>
              )}
            </div>
            {statement.status === "failed" && statement.errorMessage && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md">
                <p className="text-sm text-destructive">{statement.errorMessage}</p>
              </div>
            )}
          </div>

          {/* Extracted Metadata & Summary */}
          {extractedData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Metadata Card */}
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="text-sm font-medium text-foreground mb-3">Extracted Metadata</h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Institution</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.metadata?.institutionName || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Account Type</dt>
                    <dd className="font-medium text-foreground capitalize">
                      {extractedData.metadata?.accountType?.replace("_", " ") || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Account Number</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.metadata?.lastFourAccountNumber
                        ? `${extractedData.metadata?.lastFourAccountNumber}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Currency</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.metadata?.currency || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Statement Date</dt>
                    <dd className="font-medium text-foreground">
                      {formatSafeDate(extractedData.metadata?.statementDate)}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Billing Period</dt>
                    <dd className="font-medium text-foreground">
                      {formatSafeDate(extractedData.metadata?.billingPeriodStart)} -{" "}
                      {formatSafeDate(extractedData.metadata?.billingPeriodEnd)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Summary Card */}
              <div className="bg-muted p-4 rounded-lg border border-border">
                <h3 className="text-sm font-medium text-foreground mb-3">Statement Summary</h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Opening Balance</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.summary?.openingBalance != null
                        ? `₹${extractedData.summary?.openingBalance?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Closing Balance</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.summary?.closingBalance != null
                        ? `₹${extractedData.summary?.closingBalance?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Total Debits</dt>
                    <dd className="font-medium text-destructive">
                      {extractedData.summary?.totalDebits != null
                        ? `₹${extractedData.summary?.totalDebits?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Total Credits</dt>
                    <dd className="font-medium text-success">
                      {extractedData.summary?.totalCredits != null
                        ? `₹${extractedData.summary?.totalCredits?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div className="col-span-2 border-t border-border my-1 pt-1" />
                  <div>
                    <dt className="text-muted-foreground">Total Due</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.summary?.totalAmountDue != null
                        ? `₹${extractedData.summary?.totalAmountDue?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Due Date</dt>
                    <dd className="font-medium text-destructive">
                      {formatSafeDate(extractedData.summary?.paymentDueDate)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Min Due</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.summary?.minimumPaymentDue != null
                        ? `₹${extractedData.summary?.minimumPaymentDue?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Available Credit</dt>
                    <dd className="font-medium text-foreground">
                      {extractedData.summary?.availableCreditLimit != null
                        ? `₹${extractedData.summary?.availableCreditLimit?.toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Rewards Card (Conditional) */}
              {extractedData.rewards && (
                <div className="bg-muted p-4 rounded-lg border border-border md:col-span-2">
                  <h3 className="text-sm font-medium text-foreground mb-3">Rewards Summary</h3>
                  <dl className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Unit</dt>
                      <dd className="font-medium text-foreground capitalize">
                        {extractedData.rewards?.unit || "Points"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Opening</dt>
                      <dd className="font-medium text-foreground">
                        {extractedData.rewards?.openingBalance ?? "N/A"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Earned</dt>
                      <dd className="font-medium text-success">
                        +{extractedData.rewards?.earned ?? 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Redeemed</dt>
                      <dd className="font-medium text-destructive">
                        -{extractedData.rewards?.redeemed ?? 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Closing</dt>
                      <dd className="font-medium text-foreground">
                        {extractedData.rewards?.closingBalance ?? "N/A"}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          )}

          {/* PDF Viewer */}
          <div className="pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-foreground">PDF Document</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullScreen(true)}
                className="gap-2"
              >
                <Maximize2 className="size-4" />
                Full screen
              </Button>
            </div>
            <div className="border border-border rounded-lg overflow-hidden bg-muted">
              <div className="w-full h-[70vh]">
                <SafePdfViewer pdfUrl={apiClient.getStatementPdfUrl(statement.id)} />
              </div>
            </div>
          </div>

          {/* Transactions */}
          {transactions.length > 0 && (
            <div className="pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium text-foreground">
                  Extracted transactions ({transactions.length})
                </h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="size-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={saveExtractedData}>
                    Save changes
                  </Button>
                </div>
              </div>

              {/* Import Section */}
              <div className="mb-6 p-4 bg-muted border border-border rounded-lg">
                <label
                  htmlFor="import-account"
                  className="text-sm font-medium text-foreground block mb-2"
                >
                  Import to account
                </label>
                <div className="flex gap-2">
                  <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                    <SelectTrigger id="import-account" className="flex-1 border-input">
                      <SelectValue placeholder="Select an account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id.toString()}>
                          {account.accountName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleImport} disabled={importing || !selectedAccountId}>
                    {importing ? (
                      <LoadingSpinner className="size-4 mr-2" />
                    ) : (
                      <Upload className="size-4 mr-2" />
                    )}
                    Import
                  </Button>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted hover:bg-muted">
                      <TableHead className="font-medium text-foreground/80">Date</TableHead>
                      <TableHead className="font-medium text-foreground/80">Description</TableHead>
                      <TableHead className="font-medium text-foreground/80">Type</TableHead>
                      <TableHead className="font-medium text-foreground/80 text-right">
                        Amount
                      </TableHead>
                      <TableHead className="font-medium text-foreground/80">Reference</TableHead>
                      <TableHead className="font-medium text-foreground/80 w-[120px]">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction, index) => (
                      <TableRow
                        // biome-ignore lint/suspicious/noArrayIndexKey: transactions don't have ids yet
                        key={index}
                        className="border-border"
                      >
                        <TableCell className="text-sm text-foreground">
                          {editingIndex === index ? (
                            <Input
                              type="date"
                              value={transaction.date}
                              onChange={(e) => updateTransaction(index, "date", e.target.value)}
                              className="w-32 border-input"
                            />
                          ) : (
                            formatSafeDate(transaction.date)
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-foreground">
                          {editingIndex === index ? (
                            <Input
                              value={transaction.description}
                              onChange={(e) =>
                                updateTransaction(index, "description", e.target.value)
                              }
                              className="border-input"
                            />
                          ) : (
                            transaction.description
                          )}
                        </TableCell>
                        <TableCell>
                          {editingIndex === index ? (
                            <Select
                              value={transaction.type}
                              onValueChange={(value) => updateTransaction(index, "type", value)}
                            >
                              <SelectTrigger className="w-24 border-input">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="credit">Credit</SelectItem>
                                <SelectItem value="debit">Debit</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span
                              className={
                                transaction.type === "credit"
                                  ? "text-success text-sm"
                                  : "text-destructive text-sm"
                              }
                            >
                              {transaction.type}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm text-foreground">
                          {editingIndex === index ? (
                            <Input
                              type="number"
                              value={transaction.amount}
                              onChange={(e) =>
                                updateTransaction(index, "amount", Number(e.target.value))
                              }
                              className="w-28 text-right border-input"
                            />
                          ) : (
                            `₹${transaction.amount.toFixed(2)}`
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {editingIndex === index ? (
                            <Input
                              value={transaction.referenceNo || ""}
                              onChange={(e) =>
                                updateTransaction(index, "referenceNo", e.target.value)
                              }
                              className="w-32 border-input"
                            />
                          ) : (
                            transaction.referenceNo || "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {editingIndex === index ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingIndex(null)}
                                >
                                  Cancel
                                </Button>
                                <Button variant="ghost" size="sm" onClick={saveExtractedData}>
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                                  onClick={() => setEditingIndex(index)}
                                >
                                  <Edit2 className="size-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                                  onClick={() => deleteTransaction(index)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {statement.status === "processing" && (
            <div className="text-center py-12">
              <LoadingSpinner className="size-8 mx-auto mb-3" />
              <p className="text-muted-foreground">Extracting transactions from statement...</p>
            </div>
          )}
        </div>
      )}

      {/* Extract confirmation dialog */}
      <Dialog open={extractDialogOpen} onOpenChange={setExtractDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm extraction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Do you want to automatically upload extracted transactions to the selected account
              after extraction?
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="auto-upload-checkbox"
                checked={autoUploadAfterExtract}
                onCheckedChange={(v) => setAutoUploadAfterExtract(Boolean(v))}
              />
              <label htmlFor="auto-upload-checkbox" className="text-sm">
                Auto-upload extracted transactions
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="rename-file-checkbox"
                checked={renameFileAfterExtract}
                onCheckedChange={(v) => setRenameFileAfterExtract(Boolean(v))}
              />
              <label htmlFor="rename-file-checkbox" className="text-sm">
                Rename file to YYYY-MM.pdf
              </label>
            </div>
            <div>
              <Label htmlFor="ignore-pages-end" className="text-sm">
                Ignore pages from end
              </Label>
              <Input
                id="ignore-pages-end"
                type="number"
                min="0"
                placeholder="0"
                value={ignorePagesFromEnd ?? ""}
                onChange={(e) =>
                  setIgnorePagesFromEnd(e.target.value ? Number(e.target.value) : undefined)
                }
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Skip last X pages (e.g., T&C, ads)
              </p>
            </div>
            <ModelSelect value={aiModel} onChange={setAiModel} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setExtractDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmExtract} disabled={extracting}>
                {extracting ? <LoadingSpinner className="size-4" /> : null}
                Extract
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen PDF Dialog */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>
              {statement.month
                ? `${statement.account?.accountName} - ${formatMonth(statement.month)}`
                : statement.fileName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden">
              <SafePdfViewer pdfUrl={apiClient.getStatementPdfUrl(statement.id)} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
